import { isEmpty } from 'lodash-es';
import { FieldValues } from 'react-hook-form';
import { ICandidate, IJob, IJobCandidateInteraction, ILocation } from 'wisestep-api';

import { aiContactPreferrence } from '../../../components/filters/components/AiContactAvailabilityFilter';
import JobsCrmSmartSourceAdvanceFiltersFormProps from '../../../components/filters/models/JobsCrmSmartSourceAdvanceFiltersFormProps';
import { MarkValueProps } from '../../../components/WsCustomSlider/WsCustomRangeSlider';
import { FiltersTagProp } from '../components/filterTagList/FiltersTagList';
import { formatLocation } from '../components/helper/JobsCrmHelper';

export const sourcingTabList = [
  {
    tabName: 'AI Suggestions',
  },
  {
    tabName: 'Similar Jobs',
  },
  {
    tabName: 'Talent Pool',
  },
  {
    tabName: 'Job Boards',
  },
  {
    tabName: 'Social Search',
  },
];

export const getLocation = (value: string, job: IJob): ILocation[] => {
  if (!job?.locations?.length) return [];
  const emptyFields = {
    continent: '',
    continentCode: '',
    countryCode: '',
    stateCode: '',
    city: '',
    state: '',
    country: '',
  };

  switch (value) {
    case 'Same City as Job':
      return job.locations.map((loc) => ({
        ...emptyFields,
        city: loc?.city ?? '',
        state: loc?.state ?? '',
        country: loc?.country ?? '',
      }));

    case 'Same State as Job':
      return job.locations.map((loc) => ({
        ...emptyFields,
        state: loc?.state ?? '',
        country: loc?.country ?? '',
      }));

    case 'Same Country as Job':
      return job.locations.map((loc) => ({
        ...emptyFields,
        country: loc?.country ?? '',
      }));

    default:
      return [];
  }
};

export const JobsCrmDefaultRequest = (): IJobCandidateInteraction => ({
  timeTakenInMillis: 0,
  page: 0,
  size: 20,
  sort: ['_score,desc'],
  minSalary: 0,
  maxSalary: 0,
  radiusInMiles: 100,
  searchScope: 'EVERYTHING',
  candidateSearchTypeList: ['mapped', 'submitted', 'uploaded', 'applied'],
  jobSearchFields: ['currentJobTitle', 'mappedJobTitle', 'previousJobTitles'],
  datesUnionBy: 'UPDATED_BETWEEN',
  searchAcross: ['ACROSS_WORKSPACE'],
  query: '',
  phoneAvailability: true,
  minMatchScore: 90,
  candidateSearchType: 'All',
  emailAvailability: true,
  excludeDuplicateRecords: true,
});

export const getDefaultAISuggestionsPayload = (
  job: IJob,
  size: number
): IJobCandidateInteraction => ({
  ...JobsCrmDefaultRequest(),
  jobIds: [job?.id ?? ''],
  jobCode: job?.jobCode ?? '',
  jobMatchingContent: [job?.title ?? '', job?.description ?? ''],
  jobTitle: job?.title,
  candidateAllLocationFields: getLocation('Same State as Job', job),
  skills: job?.skills,
  size,
});

export const getUpdatedAISuggestionsPayload = (
  data: FieldValues,
  query: IJobCandidateInteraction,
  job: IJob
): IJobCandidateInteraction => {
  const updatedQuery = { ...query };
  updatedQuery.candidateName = data?.searchText ?? '';
  updatedQuery.minMatchScore = data?.relevance[0];
  updatedQuery.emailAvailability = data?.contactAvailability.includes('Email');
  updatedQuery.phoneAvailability = data?.contactAvailability.includes('Phone');
  updatedQuery.candidateAllLocationFields =
    data?.aiLocations === 'custom'
      ? data?.customLocations
      : getLocation(data.aiLocations ?? '', job);
  updatedQuery.page = 0;
  return updatedQuery;
};

export const defaultAISuggestionsFiltersValues: JobsCrmSmartSourceAdvanceFiltersFormProps = {
  aiLocations: 'Same State as Job',
  contactAvailability: Object.values(aiContactPreferrence),
  relevance: [90, 100],
  customLocations: [],
  searchText: '',
};

export const getActiveFilters = (
  filters: JobsCrmSmartSourceAdvanceFiltersFormProps,
  job: IJob
): FiltersTagProp[] => {
  const results: FiltersTagProp[] = [];
  results.push({
    label: `Relevance : ${filters.relevance[0]} - ${filters.relevance[1]} %`,
    disableRemove: true,
    id: 'Relevance#0',
  });
  if (filters?.aiLocations) {
    const locations = getLocation(filters?.aiLocations, job);
    locations.map((loc, index) =>
      results.push({ label: `Location: ${formatLocation(loc)}`, id: `aiLocations#${index}` })
    );
  }
  if (filters?.contactAvailability?.length) {
    filters?.contactAvailability.map((contact, index) =>
      results.push({ label: `Contact Available: ${contact})}`, id: `contactAvailability#${index}` })
    );
  }
  if (filters?.customLocations?.length) {
    filters?.customLocations?.map((loc, index) =>
      results.push({
        label: `Custom Location: ${formatLocation(loc)}`,
        id: `customLocations#${index}`,
      })
    );
  }

  return results;
};

export type FilterKey = keyof typeof defaultAISuggestionsFiltersValues;

export const getQueryAndFormValues = (
  removeTag: FilterKey,
  query: IJobCandidateInteraction,
  activeFilters: JobsCrmSmartSourceAdvanceFiltersFormProps,
  index: number
): {
  updatedQuery: IJobCandidateInteraction;
  updatedFormValues: JobsCrmSmartSourceAdvanceFiltersFormProps;
} => {
  const updatedQuery = { ...query };
  const updatedFormValues = { ...activeFilters };

  switch (removeTag) {
    case 'contactAvailability':
      updatedFormValues.contactAvailability = [
        ...(activeFilters?.contactAvailability ?? []).slice(0, index),
        ...(activeFilters?.contactAvailability ?? []).slice(index + 1),
      ];

      updatedQuery.emailAvailability = updatedFormValues.contactAvailability.includes('Email');
      updatedQuery.phoneAvailability = updatedFormValues.contactAvailability.includes('Phone');
      break;

    case 'customLocations':
      updatedQuery.candidateAllLocationFields = [
        ...(query?.candidateAllLocationFields ?? []).slice(0, index),
        ...(query?.candidateAllLocationFields ?? []).slice(index + 1),
      ];
      updatedFormValues.customLocations = updatedQuery.candidateAllLocationFields;
      break;

    case 'aiLocations':
      updatedQuery.candidateAllLocationFields = [
        ...(query?.candidateAllLocationFields ?? []).slice(0, index),
        ...(query?.candidateAllLocationFields ?? []).slice(index + 1),
      ];
      if (!updatedQuery.candidateAllLocationFields?.length) {
        updatedFormValues.aiLocations = '';
      }
      break;
    default:
      break;
  }

  return { updatedQuery, updatedFormValues };
};

export const getSelectedCandidatesWithPhone = (
  candidates: ICandidate[],
  selectedCandidateIds: Set<string>
): ICandidate[] =>
  candidates.filter(
    (candidate) =>
      selectedCandidateIds.has(candidate?.id ?? '') &&
      !candidate.isInDnc &&
      !isEmpty(candidate.phoneNumber)
  );

export const getSelectedCandidatesWithEmail = (
  candidates: ICandidate[],
  selectedCandidateIds: Set<string>
): ICandidate[] =>
  candidates.filter(
    (candidate) =>
      selectedCandidateIds.has(candidate?.id ?? '') &&
      !candidate.isInDnc &&
      !isEmpty(candidate.email)
  );

export const filteredNewSet = (id: string, prevSet: Set<string>): Set<string> => {
  const newSet = new Set(prevSet);
  if (newSet.has(id)) {
    newSet.delete(id);
  } else {
    newSet.add(id);
  }
  return newSet;
};

export const revalenceOptions: MarkValueProps[] = [
  { value: 30, label: '30%' },
  { value: 40, label: '40%' },
  { value: 50, label: '50%' },
  { value: 60, label: '60%' },
  { value: 70, label: '70%' },
  { value: 80, label: '80%' },
  { value: 90, label: '90%' },
  { value: 100, label: '100%' },
];
